<template>
  <div class="employer-settings">
    <SelectInput
      :model-value="situation"
      label="Situation"
      :options="[
        { label: 'En couple', value: 'couple' },
        { label: 'Seul(e)', value: 'single' },
      ]"
      @update:model-value="$emit('update:situation', $event)"
    />

    <SelectInput
      :model-value="children"
      label="À charge"
      :options="childrenOptions"
      @update:model-value="$emit('update:children', $event)"
    />

    <SelectInput
      :model-value="childAge"
      :label="childAgeLabel"
      :options="ageOptions"
      @update:model-value="$emit('update:childAge', $event)"
    />

    <div v-if="origin !== 'contract_editor'">
      <SelectInput
        :model-value="region"
        label="Vous habitez"
        :options="regions"
        @update:model-value="$emit('update:region', $event)"
      />
    </div>

    <ToggleInput
      :model-value="isCustomMonthlySalary"
      :label="customSalaryLabel"
      @update:model-value="$emit('update:isCustomMonthlySalary', $event)"
    >
      <template #hint>
        <Popup :icon="iconQuestion" :body="`<p>Si vous n'avez pas les revenus mensuels, c'est normal : il s'agit d'une information personnelle.</p><p>Mais si le parent souhaite une simulation précise, vous pouvez lui proposer :</p><ul><li>de créer un compte pour tester directement,</li><li>ou, si le contrat est déjà créé chez nous, de saisir le code contrat pour accéder à la simulation.</li></ul>`" />
      </template>
    </ToggleInput>

    <div v-if="isCustomMonthlySalary">
      <InputGroup :label="`Ressources mensuelles moyennes ${incomeReferenceYear} (net imposable)`" append="€ / mois" wrapper-class="row-cols-1 row-cols-md-2 d-flex justify-content-end">
        <input
          type="text"
          class="form-control"
          :value="averageMonthlySalary"
          @input="$emit('update:averageMonthlySalary', $event.target.value)"
        />
        <template #hint>
          <Popup :icon="iconQuestion" :body="`<p>Le revenu à indiquer correspond à celui perçu par le foyer en ${incomeReferenceYear}, tel que déclaré et notifié sur le site de la CAF.</p><p>Il s'agit du revenu annuel du couple, à diviser par 12 pour obtenir une moyenne mensuelle, si une déclaration commune a été faite.</p>`" />
        </template>
      </InputGroup>
    </div>

    <RadioGroup
      :model-value="cmgAidToggle"
      label="Bénéficiez-vous de l'aide CMG&nbsp;?"
      :options="[
        { label: 'Oui', value: 'yes' },
        { label: 'Partiel 50%', value: 'partial' },
        { label: 'Non', value: 'no' },
      ]"
      name="cmg_aid_toggle"
      @update:model-value="$emit('update:cmgAidToggle', $event)"
    >
      <template #hint>
        <Popup :icon="iconQuestion" :body="cmgAidHelpBody" />
      </template>
    </RadioGroup>

    <ToggleInput
      :model-value="otherAidToggle"
      label="Bénéficie d'aide spécifique hors CMG (comité d'entreprise, département, ville, etc.)&nbsp;?"
      input-class="other_aid_toggle"
      @update:model-value="$emit('update:otherAidToggle', $event)"
    />

    <div v-if="otherAidToggle">
      <InputGroup label="Montant de l'aide spécifique" append="€ / mois" wrapper-class="row-cols-1 row-cols-md-2 d-flex justify-content-end">
        <input
          type="text"
          class="form-control"
          :value="otherAid"
          @input="$emit('update:otherAid', $event.target.value)"
        />
      </InputGroup>
    </div>

    <ToggleInput
      :model-value="handicapToggle"
      label="Enfant en situation de handicap ?"
      input-class="handicap_toggle"
      @update:model-value="$emit('update:handicapToggle', $event)"
    >
      <template #hint>
        <Popup :icon="iconQuestion" :body="`<p>Le montant maximum du CMG est majoré de 30% pour un enfant en situation de handicap.</p>`" />
      </template>
    </ToggleInput>

    <template v-if="careType === 'ged'">
      <ToggleInput
        :model-value="sharedCostToggle"
        label="Répartir le coût pour une garde partagée&nbsp;?"
        input-class="cmg-simulator-ged__shared-cost-toggle"
        @update:model-value="$emit('update:sharedCostToggle', $event)"
      >
        <template #hint>
          <Popup :icon="iconQuestion" :body="`<p>Pour une garde partagée le coût du salarié est partagé entre les parents qui font garder leurs enfants. En activant cette option vous pourrez indiquer le pourcentage du salaire que vous souhaitez prendre à votre charge.</p>`" />
        </template>
      </ToggleInput>

      <div v-if="sharedCostToggle">
        <InputGroup label="Part du salaire à votre charge" append="%" wrapper-class="row-cols-1 row-cols-md-2 d-flex justify-content-end">
          <input
            type="text"
            class="form-control"
            :value="sharedCost"
            @input="$emit('update:sharedCost', $event.target.value)"
          />
        </InputGroup>
      </div>

      <ToggleInput
        :model-value="firstEmployeeDeclaration"
        label="Première année de déclaration fiscale d'une garde à domicile&nbsp;?"
        input-class="cmg-simulator-ged__first-employee-declaration"
        @update:model-value="$emit('update:firstEmployeeDeclaration', $event)"
      >
        <template #hint>
          <Popup :icon="iconQuestion" :body="`<p>Pour la 1re année où vous employez directement un salarié à domicile, les dépenses sont retenues dans la limite de <b>15&nbsp;000&nbsp;€</b> au lieu de <b>12&nbsp;000&nbsp;€</b>.</p><p>Ce plafond est majoré de <b>1&nbsp;500&nbsp;€</b> par enfant à charge (750€ dans le cas d'une garde partagée), sans pouvoir dépasser au total <b>18&nbsp;000&nbsp;€</b>.</p>`" />
        </template>
      </ToggleInput>
    </template>

    <div v-if="handicapToggle" class="help-notice small notice">
      <div class="bubble">
        Ce simulateur gère uniquement la majoration d'aide CMG applicable aux enfants en situation de handicap.
        Vous pouvez, selon condition, bénéficier d'exonérations supplémentaires. Pour obtenir plus d'informations à ce
        sujet nous vous invitons à vous rapprocher de votre organisme de déclaration CESU/Pajemploi.
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import {
  InputGroup,
  Popup,
  RadioGroup,
  SelectInput,
  ToggleInput,
  iconQuestion,
} from '@twg/ui';

export default defineComponent({
  name: 'EmployerSettings',
  components: { InputGroup, Popup, RadioGroup, SelectInput, ToggleInput },
  props: {
    situation: { type: String, required: true },
    children: { type: String, required: true },
    childAge: { type: String, required: true },
    region: { type: String, required: true },
    isCustomMonthlySalary: { type: Boolean, required: true },
    averageMonthlySalary: { type: String, required: true },
    cmgAidToggle: { type: String, required: true },
    otherAidToggle: { type: Boolean, required: true },
    otherAid: { type: String, required: true },
    handicapToggle: { type: Boolean, required: true },
    careType: { type: String, default: 'assmat' },
    sharedCostToggle: { type: Boolean, default: false },
    sharedCost: { type: String, default: '0' },
    firstEmployeeDeclaration: { type: Boolean, default: false },
    origin: { type: String, default: 'single_tool' },
    regions: { type: Array, default: () => [] },
    externalSources: { type: Object, default: () => ({}) },
    incomeReferenceYear: { type: Number, default: () => new Date().getFullYear() - 2 },
  },
  emits: [
    'update:situation', 'update:children', 'update:childAge', 'update:region',
    'update:isCustomMonthlySalary', 'update:averageMonthlySalary',
    'update:cmgAidToggle', 'update:otherAidToggle', 'update:otherAid',
    'update:handicapToggle', 'update:sharedCostToggle', 'update:sharedCost',
    'update:firstEmployeeDeclaration',
  ],
  setup(props) {
    const childrenOptions = Array.from({ length: 9 }, (_, i) => ({
      label: `${i + 1} enfant${i > 0 ? 's' : ''}`,
      value: String(i + 1),
    }));

    // All care types and situations keep the four brackets: the CAF
    // social-contribution caps are keyed on the real age (520 € for <3,
    // 261 € for 3–5), and single-parent GED eligibility up to 12 years is
    // already handled by childTooOld in the params composable.
    const ageOptions = [
      { label: 'Moins de 3 ans', value: '1' },
      { label: '3 à 5 ans', value: '2' },
      { label: '6 à 11 ans', value: '3' },
      { label: '12 ans ou plus', value: '4' },
    ];

    const childAgeLabel = computed(() => {
      return parseInt(props.children) > 1
        ? 'Âge du plus jeune enfant gardé au moment du contrat'
        : "Âge de l'enfant au moment du contrat";
    });

    const customSalaryLabel = computed(() => {
      return `Connaissez-vous les ressources mensuelles en ${props.incomeReferenceYear} ?`;
    });

    const cmgAidHelpBody = computed(() => {
      const sourceKey = props.careType === 'ged' ? 'pajemploi_ged_CMG' : 'pajemploi_CMG';
      const sourceLink = props.externalSources[sourceKey]
        ? ` <a href="${props.externalSources[sourceKey]}" target="_blank" rel="noopener noreferrer" class="source-link">(source)</a>`
        : '';
      if (props.careType === 'ged') {
        return '<p>Vous devez remplir les conditions ci-dessous pour pouvoir bénéficier de l\'aide CMG :</p>' +
          '<ul class="relaxed"><li>le plus jeune enfant de vos enfants gardés doit avoir moins de 6 ans,</li>' +
          '<li>vous devez avoir une activité professionnelle,</li>' +
          '<li>si vous avez recours à une association ou une entreprise, l\'enfant doit être gardé au moins 16 heures dans le mois.</li></ul>' +
          sourceLink +
          '<p class="mt-3">En cas de travail à temps partiel, <b>inférieur ou égal à 50 %</b> de son temps de travail habituel, le montant du CMG est divisé par 2.</p>';
      }
      return '<p>Vous devez remplir les conditions ci-dessous pour pouvoir bénéficier de l\'aide CMG :</p>' +
        '<ul class="relaxed"><li>le plus jeune enfant de vos enfants accueilli par l\'assistant(e) maternel(le) doit avoir moins de 6 ans ou en cas de famille monoparentale l\'aide sera prolongée jusqu\'à 12 ans,</li>' +
        '<li>vous devez avoir une activité professionnelle,</li>' +
        '<li>si vous avez recours à une association ou une entreprise, l\'enfant doit être gardé au moins 16 heures dans le mois,</li>' +
        '<li>l\'assistant(e) maternel(le) doit être agréé(e) par les services de la protection maternelle et infantile.</li></ul>' +
        sourceLink +
        '<p class="mt-3">En cas de travail à temps partiel, <b>inférieur ou égal à 50 %</b> de son temps de travail habituel, le montant du CMG est divisé par 2.</p>';
    });

    return {
      childrenOptions,
      ageOptions,
      childAgeLabel,
      customSalaryLabel,
      cmgAidHelpBody,
      iconQuestion,
    };
  },
});
</script>
